// Generic empty-state component for list / grid / dashboard views.
// Drop into any container with `extends: 'EmptyState'` and override
// the `icon`, `title`, `hint`, `action` props as needed.
//
// Pattern:
//   MyEmptyState: {
//     extends: 'EmptyState',
//     icon: 'people',
//     title: 'No team members yet',
//     hint: 'Invite teammates to start collaborating.',
//     action: { text: 'Invite member', onClick: (e, el, s) => ... }
//   }
//
// TRANSLATION — read docs/I18N-CONVENTION.md §1.2 before changing this.
// `title` is a global HTML attribute, so a `{{ … | polyglot }}` template put
// on it is resolved to '' by the static attribute pass and written back over
// the prop, and a FUNCTION value is dropped by the child's text effect. There
// is no working rewrite form — only a RENAME. So this component now reads
// `heading` / `hintText` FIRST and keeps `title` / `hint` as the back-compat
// fallback (canvas and workspace still pass those). Both paths are resolved
// through polyglot at the render site, so a call site may pass EITHER a bundle
// key ('canvas.empty.noPages') or a plain string; polyglot returns a non-key
// verbatim. New call sites: pass a KEY on `heading` / `hintText`.

// THE ERROR VARIANT (white-label redesign 2026-09-26, DESIGN_SPEC §5.8, L60):
// a page that could not load its data is a calm poster, never a red banner —
//
//   EmptyState: {
//     variant: 'error',
//     heading: 'crm.error.contacts',          // "Couldn't load contacts"
//     hintText: 'common.error.retryHint',     // "Check your connection…"
//     details: (el, s) => s.root.crmError,    // the raw message, folded away
//     action: { text: 'common.action.try_again', onClick: … }
//   }
//
// The alert glyph in `dangerInk` on the neutral plate; the one action is
// TONAL (the `layer` wash), not the ink primary — retrying is a recovery,
// not a call to action; the technical message sits behind a native "Details"
// disclosure in the `Code` face, so it is reachable and never the headline.
// The error poster is announced (role=alert). Capability refusals are not
// errors and never reach this (the view is gated instead).
export const EmptyState = {
  flow: 'y',
  align: 'center center',
  gap: 'A',
  padding: 'C A',
  width: '100%',
  textAlign: 'center',

  // Default props — consumers override.
  variant: 'empty',
  details: null,
  role: (el) => (el.variant === 'error' ? 'alert' : null),
  icon: null,
  heading: '',
  headingTail: '',
  hintText: '',
  title: '',
  hint: '',
  action: null,
  visual: null,

  // THE VISUAL SLOT (brief §4.8 item 3, P08 / P15 / P16, 2026-09-24): an
  // optional picture ABOVE the heading — an image stack or a tile preview
  // (EmptyStateImages / EmptyStateTiles below), or any definition. It takes
  // the icon's place: a poster shows one picture.
  Visual: {
    if: (el) => !!el.parent?.visual,
    flow: 'y',
    align: 'center center',
    // A definition rendered as-is: `childrenAs: 'element'`. A `content` factory
    // returning the same object mounted an EMPTY element (measured).
    childrenAs: 'element',
    children: (el) => (el.parent?.visual ? [el.parent.visual] : [])
  },

  // Decorative icon — falls back to nothing if no icon prop (the error
  // variant falls back to the alert glyph).
  IconWrap: {
    if: (el) => !!(el.parent?.icon || el.parent?.variant === 'error') && !el.parent?.visual,
    flow: 'y',
    align: 'center center',
    width: 'C',
    height: 'C',
    // A circle from the shape token (was the raw '50%'), on the neutral
    // PLATE: `overlay` is a 5% wash of the ground's own family and read
    // 1.00:1 on a common-box card (brand TabSet); `widgetPlate` steps on the
    // sheet, the card and the document (brief §4.8, 2026-09-24).
    round: 'radiusPill',
    background: 'widgetPlate',
    color: (el) => (el.parent?.variant === 'error' ? 'dangerInk' : 'caption'),
    Icon: {
      icon: (el) =>
        el.parent.parent.icon || (el.parent.parent.variant === 'error' ? 'exclMark' : null),
      boxSize: 'A1'
    }
  },

  Title: {
    tag: 'h3',
    fontSize: 'A1',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    text: (el) => el.call('polyglot', el.parent.heading || el.parent.title || ''),
    // THE TWO-TONE HEADING (brief §4.8 item 2, P16 / P35 / P45): an optional
    // quiet second half in the SAME heading. `inactive` in light; `caption`
    // in dark, because a poster may sit on the bare dark stage, where
    // `inactive` reads 3.33:1 and `caption` 6.07:1 (brief G6).
    Tail: {
      tag: 'span',
      if: (el) => !!el.parent?.parent?.headingTail,
      color: 'inactive',
      '@dark': { color: 'caption' },
      text: (el) => ' ' + el.call('polyglot', el.parent.parent.headingTail || '')
    }
  },

  Hint: {
    if: (el) => !!(el.parent?.hintText || el.parent?.hint),
    tag: 'p',
    fontSize: 'Z2',
    color: 'caption',
    margin: '0',
    maxWidth: 'J',
    text: (el) => el.call('polyglot', el.parent.hintText || el.parent.hint || '')
  },

  // The raw message of a failure, folded away (error variant only).
  ErrorDetails: {
    tag: 'details',
    hide: (el) => !(el.parent?.variant === 'error' && el.parent?.details),
    maxWidth: 'J',
    width: '100%',
    textAlign: 'left',
    ErrorSummary: {
      tag: 'summary',
      cursor: 'pointer',
      fontSize: 'Z1',
      color: 'caption',
      textAlign: 'center',
      ':hover': { color: 'title' },
      text: (el) => el.call('polyglot', 'common.action.details')
    },
    ErrorText: {
      tag: 'pre',
      margin: 'Z 0 0',
      padding: 'Z A',
      round: 'radiusControl',
      theme: 'layer',
      fontFamily: 'Code',
      fontSize: 'Y',
      color: 'caption',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      text: (el) => {
        const d = el.parent.parent.details
        return String((typeof d === 'function' ? d(el.parent.parent, el.state) : d) || '')
      }
    }
  },

  ActionBtn: {
    if: (el) => !!el.parent?.action,
    tag: 'button',
    type: 'button',
    // The one primary action of the poster moment — solid-ink capsule on the
    // control scale (DESIGN_LANGUAGE.md §6.2; primary actions are ink, never
    // hue fills — §1). The `primary` theme carries the hover/focus/active
    // state steps (Rule 65). The error variant's retry is TONAL.
    theme: 'primary',
    // flat hook: FW-ELEMENT-DEFINITION-DATA-REPLACES-EXTENDS-DATA-1
    dataVariant: (el) => el.parent?.variant || 'empty',
    '&[data-variant="error"]': { theme: 'layer', color: 'title' },
    '&[data-variant="error"]:hover': { background: 'selected' },
    minHeight: 'controlH',
    padding: '0 A',
    round: 'radiusPill',
    fontSize: 'Z2',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    marginTop: 'V',
    text: (el) => el.call('polyglot', el.parent?.action?.text || ''),
    onClick: (e, el, s) => {
      const action = el.parent?.action
      if (typeof action?.onClick === 'function') action.onClick(e, el, s)
    }
  }
}

// ── Visual presets for the slot (brief §4.8 item 3) ─────────────────────────
// A tile preview: `count` tonal tiles in a row, the shape of the content that
// will appear (P08, P16). `visual: { extends: 'EmptyStateTiles', count: 3 }`.
export const EmptyStateTiles = {
  flow: 'x',
  gap: 'Y',
  count: 3,
  childrenAs: 'props',
  children: (el) => Array.from({ length: el.count || 3 }, (_, i) => ({ key: `tile-${i}` })),
  childProps: {
    boxSize: 'C',
    round: 'radiusControl',
    background: 'widgetPlate'
  }
}

// An image stack: up to three images, each on its own layer of one grid cell,
// offset by a spacing step — no negative margin (P15).
// `visual: { extends: 'EmptyStateImages', images: [src, src, src] }`.
export const EmptyStateImages = {
  display: 'grid',
  images: [],
  childExtends: 'Img',
  children: (el) =>
    (el.images || []).slice(0, 3).map((src, i) => ({ key: `img-${i}`, src, layer: i })),
  childProps: {
    gridArea: '1 / 1',
    boxSize: 'D',
    round: 'radiusCard',
    objectFit: 'cover',
    alt: '',
    transform: (el) => `translateX(calc(var(--spacing-Z) * ${el.layer - 1}))`
  }
}

// ── Loading (brief §4.8 item 4, P18 / P49) ─────────────────────────────────
// The skeleton of the SAME poster: same outer box, and every placeholder at
// the geometry of the part it stands for — the icon's C circle, the title's
// A1 line box, two Z2 hint lines inside J, the action's controlH capsule —
// so swapping it for the EmptyState moves nothing (Rule 66). A text line is
// measured by a no-break space at the part's own font size, so its box IS
// that part's line box.
export const EmptyStateLoading = {
  flow: 'y',
  align: 'center center',
  gap: 'A',
  padding: 'C A',
  width: '100%',
  ariaBusy: 'true',

  // Each part is the shared Skeleton family by its own key.
  SkeletonCircle: { width: 'C', height: 'C', round: 'radiusPill' },
  // the title's A1 line box
  SkeletonRow: { fontSize: 'A1', height: 'auto', width: 'F2', text: '\u00a0' },
  // Two hint lines are two line boxes with NO gap between them — a gap made
  // the block 1.53px taller than a two-line hint, and the action sat 1.64px
  // low until the swap (measured live on dev, 2026-09-24). The bars get
  // their separation from a visual-only scale, which moves no layout.
  HintLines: {
    flow: 'y',
    align: 'center center',
    gap: '0',
    width: '100%',
    maxWidth: 'J',
    childProps: { fontSize: 'Z2', height: 'auto', text: '\u00a0', transform: 'scaleY(0.7)' },
    SkeletonRow_1: { width: '100%' },
    SkeletonRow_2: { width: '70%' }
  },
  // the action's capsule
  Skeleton: { minHeight: 'controlH', width: 'E1', round: 'radiusPill', marginTop: 'V' }
}
